<template>
  <div class="break-long-words dynamic-field relative">
    <lf-text-editor
      ref="textEditorComponentRef"
      :class="{ 'editor-with-counter': hasLengthCounter }"
      content-type="html"
      :value="modelValue"
      :name="name"
      :modules="modules"
      :placeholder="placeholder"
      :allow-shortcuts="allowShortcuts"
      @update:content="handleValueUpdate"
      @selection:change="handleSelectionChange"
      @editor:ready="handlePreparedEditor"
      @focus="handleFocused"
      @blur="isFocused = false"
    >
      <template v-if="showToolbar" #customToolbar>
        <span v-if="isDynamicToolbar" class="ql-formats">
          <select class="ql-header">
            <option value="1"></option>
            <option value="2"></option>
            <option value="3"></option>
            <option value="4"></option>
            <option value="5"></option>
            <option value="6"></option>
            <option value="false" selected></option>
          </select>
        </span>
        <span class="ql-formats">
          <button class="ql-bold"></button>
          <button class="ql-underline"></button>
          <button class="ql-italic"></button>
          <button v-if="isDynamicToolbar" class="ql-strike"></button>
        </span>
        <span v-if="isDynamicToolbar" class="ql-formats">
          <select class="ql-color"></select>
          <button class="ql-script" value="sub"></button>
          <button class="ql-script" value="super"></button>
        </span>
        <span class="ql-formats">
          <select v-if="showTextAlign" class="ql-align">
            <option selected></option>
            <option value="center"></option>
            <option value="right"></option>
            <option value="justify"></option>
          </select>
          <button class="ql-list" value="ordered"></button>
          <button class="ql-list" value="bullet"></button>
        </span>
        <span class="ql-formats">
          <button class="ql-link"></button>
        </span>
        <span v-if="isDynamicToolbar" class="ql-formats">
          <select class="ql-lineheight" value="ordered">
            <option
              v-for="option in WYSIWYG_TOOLBAR_LINE_HEIGHT_OPTIONS"
              :key="option"
              :value="option"
            ></option>
          </select>
          <select class="ql-marginTop" value="ordered">
            <option
              v-for="option in PARAGRAPH_MARGIN_OPTIONS"
              :key="option"
              :value="option"
            ></option>
          </select>
          <select class="ql-marginBottom" value="ordered">
            <option
              v-for="option in PARAGRAPH_MARGIN_OPTIONS"
              :key="option"
              :value="option"
            ></option>
          </select>
        </span>
        <span v-if="canUploadImage" class="ql-formats">
          <button class="ql-image" />
        </span>
        <span class="ql-formats">
          <lf-dropdown
            ref="dynamicDropdownRef"
            bold-placeholder
            :custom-button-classnames="{
              widthClassname: 'w-32!',
              headlineTextClassname: 'text-headline',
            }"
            custom-z-index="z-101"
            hide-placeholder-when-value
            is-stateless
            name="dynamic_field_dropdown"
            :options="dynamicFieldsDropdownOptions"
            :placeholder="t('COMMON.DYNAMIC_FIELDS')"
            search-enabled
            :teleport-options="{ enabled: true }"
            @change="insertDynamicField"
          ></lf-dropdown>
        </span>
      </template>
    </lf-text-editor>
    <dynamic-field-link-modal
      v-if="showLinkEditor"
      ref="modalRef"
      :value="linkEditorValue"
      :position="linkModalPosition"
      @value:save="saveLink"
      @modal:close="closeLinkModal"
      @link:remove="removeLink"
    />
    <dynamic-field-token-modal
      v-if="dynamicToken.showModal"
      :type="dynamicToken.type"
      :link-name="dynamicToken.linkName"
      :initial-params="dynamicToken.initialParams"
      @value:save="handleTokenDynamicFieldInsertion($event)"
    />
    <incoming-message
      v-if="replyToMessage"
      :content="replyToMessage"
      :is-sms="type === CommunicationType.sms"
    />
    <div
      v-if="hasLengthCounter"
      class="counter mx-2 rounded-b border border-t-0 px-4 pb-2"
    >
      <div class="relative w-2/5 max-w-50">
        <circle-progress
          :value="cleanContent.length % maxLength"
          :max-value="maxLength"
          :custom-color="UTIL_COLORS.PRIMARY"
          :size="20"
          :border-width="3"
          :border-bg-width="3"
          :custom-value="valueLengthText"
          text-small
          text-styles="font-semibold -ml-8"
        />
        <icon-base
          v-tooltip="t('ACTIVITY.SMS.MESSAGE_LENGTH_TOOLTIP')"
          :icon="IconInfo"
          class="absolute top-0-25 left-[125px] text-label"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { onClickOutside, useMagicKeys, whenever } from "@vueuse/core";
import { type Ref, computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { type Delta, Quill } from "@vueup/vue-quill";
import compact from "lodash/compact";
import { storeToRefs } from "pinia";
import ImageUploader from "quill-image-uploader";
import Mention from "quill-mention";

import { CommunicationType } from "@/enums/communicationLogs";
import { DynamicFieldModalType } from "@/enums/emailCustomization";
import { stripHTMLTagsFromString } from "@/helpers/common";
import { getDropdownOptions } from "@/helpers/common";
import { UTIL_COLORS } from "@/helpers/constants";
import {
  PARAGRAPH_MARGIN_OPTIONS,
  WYSIWYG_TOOLBAR_LINE_HEIGHT_OPTIONS,
} from "@/helpers/constants";
import { UTM_DYNAMIC_PARAMETERS_MAP } from "@/helpers/constants/tracking";
import {
  handleDenotationTags,
  removeAttributesFromPayload,
  zeroWidthSpaceCharRegex,
} from "@/helpers/textEditor";
import { useSystem } from "@/hooks/common";
import { useEditorStore } from "@/stores/editor";

import DynamicFieldLinkModal from "@/components/dynamicFields/DynamicFieldLinkModal.vue";
import DynamicFieldTokenModal from "@/components/dynamicFields/DynamicFieldTokenModal.vue";
import IncomingMessage from "@/components/emailSending/IncomingMessage.vue";
import IconInfo from "@/components/icons/IconInfo.vue";
import CircleProgress from "@/components/ui/CircleProgress.vue";
import LfTextEditor from "@/components/ui/LfTextEditor.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";

import type { TokenModalPayload } from "@/components/dynamicFields/DynamicFieldTokenModal.vue";
import type { EmailTemplateDynamicField } from "@/models/options";
import type { MaybeElement } from "@vueuse/core";

type ToolbarType = "simple" | "dynamic";

const {
  modelValue,
  placeholder = "",
  showToolbar = false,
  name,
  canUploadImage = false,
  hasLengthCounter = false,
  maxLength = 0,
  type = CommunicationType.email,
  toolbarType = "simple",
  replyToMessage,
  allowShortcuts = false,
} = defineProps<{
  modelValue: string;
  placeholder?: string;
  showToolbar?: boolean;
  name: string;
  canUploadImage?: boolean;
  hasLengthCounter?: boolean;
  maxLength?: number;
  type?: CommunicationType;
  toolbarType?: ToolbarType;
  showTextAlign?: boolean;
  replyToMessage?: string;
  allowShortcuts?: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: string];
  "file:upload": [File];
  "clean-content:update": [value: string];
  submit: [];
}>();

const { t } = useI18n();
const editorStore = useEditorStore();
const { getters } = useStore();
const { enter } = useMagicKeys();
const { systemMetaKey } = useSystem();
const { isFocused } = storeToRefs(editorStore);

const MAX_TAGS_DROPDOWN_ELEMENTS = 13;
const DROPDOWN_TAG_HEIGHT = 30;
const maxTagsDropdownHeight = MAX_TAGS_DROPDOWN_ELEMENTS * DROPDOWN_TAG_HEIGHT;

const textEditorComponentRef = ref<InstanceType<typeof LfTextEditor> | null>(
  null
);
const dynamicDropdownRef = ref<InstanceType<typeof LfDropdown> | null>(null);
const modalRef = ref<InstanceType<typeof DynamicFieldLinkModal> | null>(null);

const typedTag = ref("");
const hasRenderedTagList = ref(false);
const dropdownElement = ref<HTMLElement | null>(null);
const linkWord = ref<{ index: number; length: number } | null>(null);
const showLinkEditor = ref(false);
const linkEditorValue = ref("");
const linkModalPosition = ref({
  bottom: 0,
  left: 0,
  right: 0,
  top: 0,
  height: 0,
});
const cleanContent = ref("");
const dynamicToken = ref<{
  isChecked: boolean;
  showModal: boolean;
  item: EmailTemplateDynamicField | null;
  position: number;
  type: DynamicFieldModalType;
  linkName: string;
  isEditing: boolean;
  initialParams: string;
}>({
  isChecked: false,
  showModal: false,
  item: null,
  position: 0,
  type: DynamicFieldModalType.Resume,
  linkName: "",
  isEditing: false,
  initialParams: "",
});

const valueLengthText = computed(() => {
  if (type === CommunicationType.sms) {
    const numberOfSms = Math.ceil(cleanContent.value.length / maxLength);
    return `${cleanContent.value.length} / ${maxLength} - ${numberOfSms} ${t(
      "COMMON.SMS"
    )}`;
  }

  return "";
});

const handleFocused = () => setTimeout(() => (isFocused.value = true), 0);

const handleSelectionChange = () => {
  const editorHasFocus =
    textEditorComponentRef.value?.editorInstance.hasFocus();

  if (showLinkEditor.value || !editorHasFocus) {
    return;
  }

  const editorInstance = textEditorComponentRef.value?.editorInstance;
  const selection = editorInstance.getSelection();
  const cursorPosition = selection?.index;
  const selectionFormat = editorInstance.getFormat();

  if (!selectionFormat?.link) {
    if (!selection.length) {
      editorInstance.setSelection(cursorPosition);
    }

    return;
  }

  const contents: Delta = editorInstance.getContents();

  const content = contents?.ops.find(
    (content) =>
      content.attributes?.link &&
      content.attributes?.link === selectionFormat.link
  );

  if (!content) {
    return;
  }

  editorInstance.setSelection(cursorPosition);

  const text = editorInstance.getText();

  let linkWordStart = text?.indexOf(content.insert);

  while (linkWordStart !== -1) {
    if (
      (cursorPosition >= linkWordStart &&
        cursorPosition <= linkWordStart + content.insert?.toString()?.length) ||
      0
    ) {
      break;
    }
    linkWordStart = text?.indexOf(content.insert, linkWordStart + 1);
  }

  if (linkWordStart === -1) {
    return;
  }

  linkWord.value = {
    index: linkWordStart,
    length: content.insert?.toString()?.length || 0,
  };

  linkEditorValue.value = selectionFormat.link;
  showLinkEditor.value = true;
};

const dynamicFields = computed<EmailTemplateDynamicField[] | undefined>(
  () => getters["options/dynamicFields"]?.fields || undefined
);

const filteredDynamicFields = computed(() => {
  if (!dynamicFields.value) {
    return [];
  }

  return dynamicFields.value.filter(
    (option) =>
      option.allowed_templates.length === 0 ||
      option.allowed_templates.includes(type)
  );
});

const dynamicFieldsDropdownOptions = computed(() => {
  return getDropdownOptions(
    filteredDynamicFields.value.map((option) => ({
      id: option.name,
      name: option.name,
    }))
  );
});

const modules = computed(() =>
  compact([
    {
      name: "mention",
      module: Mention,
      blotName: "styledMention",
      options: {
        allowedChars: /^[A-Za-z\s]*$/,
        mentionDenotationChars: ["["],
        dataAttributes: ["tag", "name"],
        blotName: "styledMention",
        source: function (
          searchTerm: string,
          renderList: (a: EmailTemplateDynamicField[], b: string) => void,
          dynamicChar: string
        ) {
          if (dynamicChar !== "[") {
            return;
          }

          const values = filteredDynamicFields.value?.map((field) => ({
            ...field,
            value: field.name,
          }));

          if (!values?.length) {
            return;
          }

          typedTag.value = searchTerm;

          if (searchTerm.length === 0) {
            renderList(values, searchTerm);
            dropdownElement.value = document.querySelector(
              ".ql-mention-list"
            ) as HTMLElement;
            if (dropdownElement.value) {
              dropdownElement.value.style.height = maxTagsDropdownHeight + "px";
              dropdownElement.value.style.minWidth = "280px";
            }
            // has to be rendered the second time on the very first load
            if (!hasRenderedTagList.value) {
              renderList(values, searchTerm);
            }
            return;
          }

          const matches = values.filter((field) =>
            field.name.toLowerCase().includes(searchTerm.toLowerCase())
          );

          if (dropdownElement.value) {
            dropdownElement.value.style.height =
              matches.length < 13
                ? matches.length * 30 + "px"
                : maxTagsDropdownHeight + "px";
            dropdownElement.value.style.minWidth = "280px";
          }

          renderList(matches, searchTerm);
        },
        onSelect: function (item: EmailTemplateDynamicField) {
          if (!textEditorComponentRef.value?.editorInstance) {
            return;
          }
          const editorInstance = textEditorComponentRef.value.editorInstance;
          const cursorPosition = editorInstance.getSelection()?.index;

          if (cursorPosition !== 0 && !cursorPosition) {
            return;
          }

          editorInstance.deleteText(
            cursorPosition - typedTag.value.length - 1,
            typedTag.value.length + 1
          );
          handleDynamicFieldInsertion(
            item,
            cursorPosition - typedTag.value.length - 1
          );
          editorInstance.setSelection(cursorPosition + 1);
        },
      },
    },
    canUploadImage && {
      name: "imageUploader",
      module: ImageUploader,
      options: {
        upload: (file: File) => {
          emit("file:upload", file);
        },
      },
    },
  ])
);

const isDynamicToolbar = computed(() => toolbarType === "dynamic");

const closeLinkModal = () => {
  showLinkEditor.value = false;
  linkEditorValue.value = "";
};

onClickOutside(modalRef as Ref<MaybeElement>, closeLinkModal);

const resetLinkState = () => {
  linkEditorValue.value = "";
  showLinkEditor.value = false;
  linkWord.value = null;
};

const removeLink = () => {
  if (!linkWord.value) {
    return;
  }
  textEditorComponentRef.value?.editorInstance.removeFormat(
    linkWord.value.index,
    linkWord.value.length
  );

  resetLinkState();
};

const saveLink = (link: string) => {
  linkEditorValue.value = link;

  if (!linkWord.value) {
    resetLinkState();
    return;
  }

  const linkWordLastLetterIndex = linkWord.value.index + linkWord.value.length;
  const editorInstance = textEditorComponentRef.value?.editorInstance;

  if (!link.length) {
    editorInstance.setSelection(linkWordLastLetterIndex);
    resetLinkState();
    return;
  }

  const word = editorInstance.getText(
    linkWord.value.index,
    linkWord.value.length
  );

  editorInstance.insertText(
    linkWord.value.index,
    word,
    "link",
    linkEditorValue.value
  );

  editorInstance.setSelection(linkWordLastLetterIndex);

  editorInstance.deleteText(linkWordLastLetterIndex, word.length);

  resetLinkState();
};

const getLinkModalType = (tag: string): DynamicFieldModalType | undefined => {
  if (tag.includes(DynamicFieldModalType.Resume)) {
    return DynamicFieldModalType.Resume;
  }
  if (tag.includes(DynamicFieldModalType.Widget)) {
    return DynamicFieldModalType.Widget;
  }
  if (tag.includes("link.") && !tag.includes("calendar_link")) {
    return DynamicFieldModalType.Link;
  }
  return undefined;
};

const handleDynamicFieldInsertion = (
  item: EmailTemplateDynamicField,
  position: number
) => {
  const editorInstance = textEditorComponentRef.value?.editorInstance;

  if (!editorInstance) {
    return;
  }

  const modalType = getLinkModalType(item.tag);

  if (modalType && !dynamicToken.value.isChecked) {
    dynamicToken.value.isChecked = true;
    dynamicToken.value.showModal = true;
    dynamicToken.value.item = item;
    dynamicToken.value.position = position;
    dynamicToken.value.type = modalType;
    dynamicToken.value.linkName = formatLinkName(item.name);
    dynamicToken.value.initialParams = "";
    return;
  }

  dynamicToken.value.isChecked = false;
  dynamicToken.value.item = null;

  editorInstance.insertEmbed(position, "styledMention", item);

  // Add space after the dynamic field
  editorInstance.insertText(position + 1, " ");

  handleDenotationTags();
};

const handleTokenDynamicFieldInsertion = (payload: TokenModalPayload) => {
  dynamicToken.value.showModal = false;

  if (!dynamicToken.value.item) {
    return;
  }

  // When editing and user cancels, keep the original mention untouched
  if (dynamicToken.value.isEditing && !payload.value) {
    dynamicToken.value.isEditing = false;
    dynamicToken.value.position = 0;
    return;
  }

  const editorInstance = textEditorComponentRef.value?.editorInstance;

  // When editing, remove the old mention before inserting the updated one
  if (dynamicToken.value.isEditing) {
    editorInstance.deleteText(dynamicToken.value.position, 1);
    dynamicToken.value.isEditing = false;
  }

  dynamicToken.value.item.tag = payload.value
    ? getUpdatedResumeLinkTag(dynamicToken.value.item.tag, payload.value)
    : dynamicToken.value.item.tag;

  if (payload.applyToAll && payload.urlParamTokens.length) {
    applyUrlParamsToAllLinks(payload.urlParamTokens);
  }

  handleDynamicFieldInsertion(
    dynamicToken.value.item,
    dynamicToken.value.position
  );

  editorInstance.setSelection(dynamicToken.value.position + 2);

  dynamicToken.value.position = 0;
};

const getUpdatedResumeLinkTag = (tag: string, value: string) => {
  const tagParts = tag.split("__");
  const tagName = tagParts[0];
  if (tagName.includes("]")) {
    return `${tagName.replace("]", `${value}]`)}`;
  }
  return `${tagName}${value}]`;
};

const LINK_PREFIX_PATTERN = /^link\./;
const SEPARATOR_PATTERN = /[._]/g;
const FIRST_LETTER_PATTERN = /\b\w/g;

const formatLinkName = (name: string) => {
  return name
    .replace(LINK_PREFIX_PATTERN, "")
    .replace(SEPARATOR_PATTERN, " ")
    .replace(FIRST_LETTER_PATTERN, (char) => char.toUpperCase());
};

const URL_PARAM_KEYS = Object.values(UTM_DYNAMIC_PARAMETERS_MAP).map((value) =>
  value.replace("=", "")
);

const applyUrlParamsToAllLinks = (urlParamTokens: string[]) => {
  const editorInstance = textEditorComponentRef.value?.editorInstance;
  if (!editorInstance || !urlParamTokens.length) {
    return;
  }

  const contents = editorInstance.getContents();
  const linkMentions: {
    index: number;
    data: Record<string, string>;
  }[] = [];

  let currentIndex = 0;
  for (const op of contents.ops) {
    if (
      op.insert?.styledMention &&
      op.insert.styledMention.tag?.includes("link.")
    ) {
      linkMentions.push({
        index: currentIndex,
        data: { ...op.insert.styledMention },
      });
    }
    currentIndex += typeof op.insert === "string" ? op.insert.length : 1;
  }

  // Process in reverse order to preserve indices
  for (const mention of linkMentions.reverse()) {
    const tag = mention.data.tag;
    const parts = tag.split("__");
    const baseTag = parts[0];

    // Keep only non-URL params (widget settings, tracking tokens)
    let existingNonUrlParams: string[] = [];
    if (parts[1]) {
      const paramStr = parts[1].replace(/]$/, "");
      existingNonUrlParams = paramStr.split(",").filter((p) => {
        const key = p.split("=")[0];
        return (
          !URL_PARAM_KEYS.includes(key) &&
          !p.startsWith("utm_") &&
          key !== "other"
        );
      });
    }

    const allParams = [...existingNonUrlParams, ...urlParamTokens];
    const cleanBase = baseTag.replace(/]$/, "");
    const updatedTag = allParams.length
      ? `${cleanBase}__${allParams.join(",")}]`
      : `${cleanBase}]`;

    mention.data.tag = updatedTag;

    editorInstance.deleteText(mention.index, 1);
    editorInstance.insertEmbed(mention.index, "styledMention", mention.data);
  }

  handleDenotationTags();
};

const insertDynamicField = (text: string | number) => {
  const item = dynamicFields.value?.find((field) => field.name === text);
  if (typeof text !== "string" || !text.length || !item) {
    return;
  }

  const editorInstance = textEditorComponentRef.value?.editorInstance;
  editorInstance.focus();
  const cursorPosition = editorInstance.getSelection()?.index;
  handleDynamicFieldInsertion(item, cursorPosition);
  editorInstance.setSelection(cursorPosition + 2);

  clearDynamicDropdownSearchTerm();
};

const clearDynamicDropdownSearchTerm = () => {
  if (!dynamicDropdownRef.value?.search) {
    return;
  }

  dynamicDropdownRef.value.search = "";
};

const handleValueUpdate = (value: string) => {
  const contents: Delta =
    textEditorComponentRef.value?.editorInstance.getContents();
  const hasContent = contents?.ops.some(
    (content) => content.insert !== "\n" && content.insert !== ""
  );
  if (hasContent) {
    updateCleanContent(value);
    emit("update:modelValue", removeAttributesFromPayload(value));
    return;
  }
  updateCleanContent("");
  emit("update:modelValue", "");
};

const updateCleanContent = (value: string) => {
  cleanContent.value = stripHTMLTagsFromString(
    value,
    true,
    type === CommunicationType.sms ? true : false
  ).replace(zeroWidthSpaceCharRegex, "");

  emit("clean-content:update", cleanContent.value);
};

const handlePreparedEditor = () => {
  const visibleLength = 35;
  const dynamicFieldsElements = document.querySelectorAll(
    ".ql-dynamicfields .ql-picker-item"
  );

  dynamicFieldsElements.forEach((element) => {
    const contentLength = window.getComputedStyle(element, "::before").content
      .length;

    if (contentLength > visibleLength) {
      element.classList.add("overflow");
    }
  });
};

const handleClickLink = () => {
  const editorInstance = textEditorComponentRef.value?.editorInstance;
  linkWord.value = editorInstance?.getSelection();
  linkModalPosition.value = editorInstance.getBounds(linkWord.value);
  if (!linkWord.value?.length) {
    return;
  }
  showLinkEditor.value = true;
};

const handleEditLinkTag = (event: MouseEvent) => {
  const editBtn = (event.target as HTMLElement).closest(".quill-tag-link-edit");
  if (!editBtn) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  const mentionSpan = editBtn.closest(".mention") as HTMLElement | null;
  if (!mentionSpan) {
    return;
  }

  const tag = mentionSpan.getAttribute("data-tag") || "";
  const name = mentionSpan.getAttribute("data-value") || "";

  const modalType = getLinkModalType(tag);

  if (!modalType) {
    return;
  }

  const editorInstance = textEditorComponentRef.value?.editorInstance;
  const blot = Quill.find(mentionSpan);
  const blotIndex = editorInstance.getIndex(blot);

  const [baseTag, paramsPart = ""] = tag.split("__");
  const item: EmailTemplateDynamicField = {
    name,
    tag: baseTag.includes("]") ? baseTag : `${baseTag}]`,
    allowed_templates: [],
  };

  dynamicToken.value.isChecked = true;
  dynamicToken.value.showModal = true;
  dynamicToken.value.isEditing = true;
  dynamicToken.value.item = item;
  dynamicToken.value.position = blotIndex;
  dynamicToken.value.type = modalType;
  dynamicToken.value.linkName = formatLinkName(name);
  dynamicToken.value.initialParams = paramsPart;
};

onMounted(() => {
  const editor = textEditorComponentRef.value?.editorInstance;
  editor.getModule("toolbar")?.addHandler("link", handleClickLink);

  editor?.getModule("toolbar")?.addHandler("align", (value: string) => {
    const selection = editor.getSelection();
    if (selection && selection.length > 0) {
      editor.format("align", value);
    }
  });

  editor?.root.addEventListener("click", handleEditLinkTag);
});

onBeforeUnmount(() => {
  const editor = textEditorComponentRef.value?.editorInstance;
  editor?.root.removeEventListener("click", handleEditLinkTag);
});

whenever(enter, () => {
  if (systemMetaKey.value) {
    emit("submit");
  }
});

defineExpose({ clearDynamicDropdownSearchTerm });
</script>
<style>
@reference "@/assets/tailwind.css";

.ql-picker.ql-dynamicfields {
  width: 150px;
}

.ql-picker.ql-lineheight,
.ql-picker.ql-marginTop,
.ql-picker.ql-marginBottom {
  width: 24px;
  @apply relative;
}

.ql-picker.ql-lineheight svg,
.ql-picker.ql-marginTop svg,
.ql-picker.ql-marginBottom svg {
  width: 0 !important;
}

.ql-picker.ql-lineheight::before {
  width: 22px;
  height: 27.5px;
  background: url("/icons/LineHeight.svg") no-repeat;
  content: "";
  @apply pointer-events-none absolute top-0 right-0 z-3 cursor-pointer;
}

.ql-picker.ql-marginTop::before {
  width: 18px;
  height: 23.5px;
  background: url("/icons/MarginTop.svg") no-repeat;
  content: "";
  @apply pointer-events-none absolute top-0 right-0 z-3 cursor-pointer;
}
.ql-picker.ql-marginBottom::before {
  width: 18px;
  height: 23.5px;
  background: url("/icons/MarginBottom.svg") no-repeat;
  content: "";
  @apply pointer-events-none absolute top-0 right-0 z-3 cursor-pointer;
}

.ql-snow .ql-picker.ql-lineheight .ql-picker-item::before,
.ql-snow .ql-picker.ql-marginTop .ql-picker-item::before,
.ql-snow .ql-picker.ql-marginBottom .ql-picker-item::before {
  content: attr(data-value);
}

.ql-snow .ql-picker.ql-lineheight .ql-picker-item:first-of-type::before,
.ql-snow .ql-picker.ql-marginTop .ql-picker-item:first-of-type::before,
.ql-snow .ql-picker.ql-marginBottom .ql-picker-item:first-of-type::before {
  content: "reset";
}

.ql-picker-options {
  width: 250px;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 50 !important;
}

.ql-mention-list {
  @apply relative z-100 overflow-y-auto bg-white shadow-lg;
}

.ql-mention-list-item {
  @apply px-2 py-1 text-sm text-headline hover:bg-gray-50;
}

.ql-mention-list-item.selected {
  @apply bg-gray-50;
}

.quill-tag-business,
.quill-tag-contact,
.quill-tag-integration_partner,
.quill-tag-personal_information,
.quill-tag-other {
  @apply rounded-sm border border-green-200 px-1;
  background-color: rgba(0, 128, 0, 0.06);
}

.quill-tag-link {
  @apply inline-flex items-center gap-1 rounded-sm border border-blue-200 px-1;
  background-color: rgba(59, 130, 246, 0.08);
}

.quill-tag-link-edit {
  @apply inline-flex cursor-pointer items-center;
  color: rgba(59, 130, 246, 0.8);
}

.quill-tag-link-edit:hover {
  color: rgba(59, 130, 246, 1);
}

.email-templates-edit .ql-container {
  height: auto;
  min-height: 100%;
  max-height: unset;
}
.email-templates-edit .ql-container .ql-editor {
  max-height: unset;
  height: inherit;
}

.dynamic-field.sms-headless {
  @apply flex flex-col;
  height: calc(100% - 53px);
}

.ql-toolbar.ql-snow {
  padding-bottom: 0;
}

.ql-toolbar.ql-snow .ql-formats {
  margin-bottom: 8px;
}

.ql-toolbar.ql-snow {
  svg .ql-stroke,
  svg line.ql-stroke,
  svg polyline.ql-stroke {
    @apply stroke-label;
  }
}
.ql-toolbar.ql-snow svg .ql-fill {
  @apply fill-label;
}
.ql-toolbar.ql-snow svg .ql-color-label.ql-transparent {
  @apply opacity-100;
}
.ql-toolbar.ql-snow .ql-picker-label {
  @apply text-label;
}
.ql-toolbar.ql-snow .ql-header .ql-picker-label {
  @apply rounded-sm border border-divider;
}

.email-headless .ql-toolbar {
  display: none;
}

.sms-template .ql-toolbar,
.sms-headless .ql-toolbar {
  @apply border-b-0;
}

.sms-headless .ql-toolbar {
  @apply mx-2;
}

.dynamic-field.sms-headless .ql-container,
.dynamic-field.sms-template .ql-container {
  border-radius: 0.375rem;
  border-top: 1px solid rgb(229, 231, 235) !important;
  @apply mx-2 max-h-none rounded-b-none border-b-0 py-2-5 pr-7 pl-1;
}

.dynamic-field.sms-conversation .ql-container {
  border-bottom: none;
  @apply mx-2 max-h-none rounded-md rounded-b-none border-b-0 border-white/50 py-2-5 pl-2;
}

.dynamic-field.sms-conversation .counter {
  @apply border-white/50;
}

.dynamic-field.sms-headless .ql-container {
  @apply rounded-t-none;
}

.dynamic-field.sms-template .ql-container,
.dynamic-field.sms-template .counter {
  @apply mx-0 rounded-none;
}

.dynamic-field.sms-headless .ql-editor,
.dynamic-field.sms-template .ql-editor,
.dynamic-field.sms-conversation .ql-editor {
  min-height: 45px;
  max-height: unset;
  height: inherit;
}

.dynamic-field.sms-conversation .ql-editor {
  @apply p-0;
}

.dynamic-field .editor-with-counter {
  @apply h-full;
}

.dynamic-field.email-headless .ql-container {
  border-radius: 0.375rem;
  border-color: rgb(229, 231, 235);
  border-top: 1px solid rgb(229, 231, 235) !important;
  min-height: 45px !important;
  @apply py-2-5 pr-7 pl-2-5;
}

.email-headless.email-headless__xs.ql-container {
  min-height: 15px !important;
  @apply py-1-5 pr-3 pl-1-5;
}

.dynamic-field.email-headless .ql-container .ql-editor {
  padding: 0;
  min-height: unset;
  @apply text-sm text-headline;
}

.dynamic-field.sms-conversation .ql-container {
  min-height: unset !important;
  border-top: 1px solid rgba(255, 255, 255, 0.5) !important;
}

.email-headless .ql-editor.ql-blank::before {
  font-style: normal;
  opacity: 0.6;
  left: 9px;
}

.sms-conversation .ql-editor.ql-blank::before {
  color: white;
  opacity: 0.6;
  font-style: normal;
  left: 9px;
  top: 9px;
}

.dynamic-field__body .ql-tooltip:has(.ql-preview) {
  display: none;
}

.sms-template .text-red-700 {
  @apply border-x px-4 pt-0 pb-2;
}
.ql-align .ql-picker-options {
  width: 35px;
}

.ql-snow.ql-toolbar button.dropdown-button {
  @apply border border-woods-green/40 bg-woods-green/10 py-0;
  span:first-child {
    @apply text-headline;
  }
  span.dropdown-icon {
    @apply text-woods-green;
  }
}
</style>
